// --- Dependencies -------------------------------------------------------------------------------
import { useRef, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import { formatBytes } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    files: File[];
    onChange: (files: File[]) => void;
    /** `false` for the single-file slots — a contract has one paper, a unit gallery has many. */
    multiple?: boolean;
    /** Narrows the picker to one category. Empty means everything the drive accepts. */
    accept?: string;
    label?: string;
    disabled?: boolean;
};

/**
 * The one file picker in the app. Every form that will ever take a file — the project gallery, the
 * contract paper, the installment receipt — uses this rather than its own `<input type="file">`, so
 * drag and drop, the chosen list and the size readout behave the same everywhere.
 *
 * It holds `File` objects and never uploads: `useFetch` turns them into form-data when a call carries
 * one, and the screen decides which endpoint they go to.
 */
export default function FileInput({ files, onChange, multiple = true, accept, label, disabled }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const inputRef = useRef<HTMLInputElement>(null);
    const [isOver, setIsOver] = useState(false);

    const take = (picked: FileList | null) => {
        if (!picked?.length) return;
        const next = Array.from(picked);
        onChange(multiple ? [...files, ...next] : next.slice(0, 1));
    };

    const drop = (event: React.DragEvent) => {
        event.preventDefault();
        setIsOver(false);
        if (!disabled) take(event.dataTransfer.files);
    };

    return (
        <div className="file-input">
            {label && <span className="file-input__label">{label}</span>}

            <button
                type="button"
                className={`file-input__drop${isOver ? " file-input__drop--over" : ""}`}
                onClick={() => inputRef.current?.click()}
                onDragOver={(event) => {
                    event.preventDefault();
                    setIsOver(true);
                }}
                onDragLeave={() => setIsOver(false)}
                onDrop={drop}
                disabled={disabled}
            >
                <Icon name="upload" size={26} />
                <span className="file-input__pick">{t.drive.pick}</span>
                <span className="file-input__hint">{t.drive.pickHint}</span>
            </button>

            <input
                ref={inputRef}
                type="file"
                className="file-input__native"
                multiple={multiple}
                accept={accept}
                disabled={disabled}
                onChange={(event) => {
                    take(event.target.files);
                    // Cleared so picking the same file twice in a row still fires a change.
                    event.target.value = "";
                }}
            />

            {files.length > 0 && (
                <ul className="file-input__list">
                    {files.map((file, index) => (
                        <li key={`${file.name}-${index}`} className="file-input__item">
                            <span className="file-input__name">{file.name}</span>
                            <span className="file-input__size">{formatBytes(file.size, lang)}</span>
                            <button
                                type="button"
                                className="file-input__drop-one"
                                onClick={() => onChange(files.filter((_, at) => at !== index))}
                                aria-label={t.common.close}
                                disabled={disabled}
                            >
                                <Icon name="close" size={14} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
